import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, List, Modal, Space, Spin, Typography } from 'antd';
import { getStaticIcons, type StaticIconGroup, type StaticIconItem } from '@/services/static-icons';
import resolveBackendResourceUrl from '@/utils/backendResource';

interface StaticIconSelectorProps {
  group: StaticIconGroup;
  value?: string; // selected backend path, e.g. /static/icons/...
  onChange?: (path: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const StaticIconSelector: React.FC<StaticIconSelectorProps> = ({
  group,
  value,
  onChange,
  placeholder = '选择图标',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<StaticIconItem[]>([]);

  const filtered = useMemo(() => {
    const kw = search.trim().toLowerCase();
    if (!kw) return items;
    return items.filter((it) => it.name.toLowerCase().includes(kw) || it.path.toLowerCase().includes(kw));
  }, [items, search]);

  const selectedUrl = resolveBackendResourceUrl(value);

  const openModal = async () => {
    setOpen(true);
    if (items.length === 0) {
      setLoading(true);
      try {
        const res = await getStaticIcons(group);
        if (Array.isArray(res?.data)) {
          setItems(res.data);
        } else if (Array.isArray((res as any)?.list)) {
          setItems((res as any).list);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePick = (path: string) => {
    onChange?.(path);
    setOpen(false);
  };

  const handleClear = () => onChange?.('');

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {value ? (
        <Space style={{ width: '100%', alignItems: 'center' }}>
          <img
            src={selectedUrl}
            alt="selected icon"
            style={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 8, border: '1px solid #f0f0f0' }}
          />
          <Typography.Text type="secondary" ellipsis style={{ maxWidth: 360 }}>
            {value}
          </Typography.Text>
          <Button danger size="small" onClick={handleClear} disabled={disabled}>
            清除
          </Button>
        </Space>
      ) : null}

      <Button onClick={openModal} disabled={disabled}>
        {value ? '更换' : placeholder}
      </Button>

      <Modal
        title="选择静态图标"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => setOpen(false)}
        footer={null}
        width={720}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Input.Search
            placeholder="搜索图标名称"
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {loading ? (
            <Spin />
          ) : (
            <List
              grid={{ gutter: 16, xs: 2, sm: 3, md: 4 }}
              dataSource={filtered}
              renderItem={(item) => {
                const url = resolveBackendResourceUrl(item.path);
                return (
                  <List.Item>
                    <Space direction="vertical" align="center" style={{ width: '100%' }}>
                      <img
                        src={url}
                        alt={item.name}
                        style={{ width: 80, height: 80, objectFit: 'contain', borderRadius: 8, border: '1px solid #f0f0f0' }}
                      />
                      <Typography.Text style={{ maxWidth: 150 }} ellipsis>
                        {item.name}
                      </Typography.Text>
                      <Button size="small" type="primary" onClick={() => handlePick(item.path)}>
                        选择
                      </Button>
                    </Space>
                  </List.Item>
                );
              }}
            />
          )}
        </Space>
      </Modal>
    </Space>
  );
};

export default StaticIconSelector;
